<script setup>
import { computed } from 'vue';

import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

import {
  getDocumentExcerpt,
  formatFileSize,
  knowledgeContent,
  indexStatusLabel,
  indexStatusBadgeClass,
} from '../../helpers/kraftchatAIKnowledgeConfig';
import KraftchatAIKnowledgeForm from './KraftchatAIKnowledgeForm.vue';
import KraftchatAIKnowledgePdfEditForm from './KraftchatAIKnowledgePdfEditForm.vue';

const props = defineProps({
  document: {
    type: Object,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'update', 'delete', 'cancelEdit']);

const isPdf = computed(() => props.document.content_source === 'pdf');

const originLabel = computed(() =>
  isPdf.value
    ? knowledgeContent.pdfSourceLabel
    : knowledgeContent.textSourceLabel
);

const originDetail = computed(() => {
  if (!isPdf.value) return '';

  const size = props.document.pdf_file_size
    ? ` (${formatFileSize(props.document.pdf_file_size)})`
    : '';

  return `${props.document.pdf_file_name || ''}${size}`;
});

const formatUpdatedAt = value => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};
</script>

<template>
  <article class="rounded-xl border border-n-weak bg-n-background p-4">
    <KraftchatAIKnowledgePdfEditForm
      v-if="isEditing && isPdf"
      :document="document"
      :is-saving="isSaving"
      @save="emit('update', document, $event)"
      @cancel="emit('cancelEdit')"
    />

    <KraftchatAIKnowledgeForm
      v-else-if="isEditing"
      mode="edit"
      :document="document"
      :is-saving="isSaving"
      @save="emit('update', document, $event)"
      @cancel="emit('cancelEdit')"
    />

    <div v-else class="flex items-start justify-between gap-4">
      <div class="min-w-0 space-y-2">
        <div class="flex flex-wrap items-center gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ document.title }}
          </h4>

          <span
            class="rounded-md px-2 py-0.5 text-xs font-medium"
            :class="
              document.active
                ? 'bg-n-brand/10 text-n-brand'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{
              document.active
                ? knowledgeContent.activeStatus
                : knowledgeContent.inactiveStatus
            }}
          </span>

          <span
            class="rounded-md px-2 py-0.5 text-xs font-medium"
            :class="indexStatusBadgeClass(document.index_status)"
            :title="
              document.index_status === 'failed' ? document.index_error : ''
            "
          >
            {{ indexStatusLabel(document.index_status) }}
          </span>
        </div>

        <p class="text-xs font-medium text-n-slate-10">
          {{ originLabel }}
          <template v-if="isPdf"> · {{ originDetail }}</template>
        </p>

        <p v-if="!isPdf" class="text-sm leading-6 text-n-slate-11">
          {{ getDocumentExcerpt(document.content) }}
        </p>

        <p class="text-xs font-medium text-n-slate-10">
          {{ knowledgeContent.updatedAtLabel }}
          {{ formatUpdatedAt(document.updated_at) }}
        </p>
      </div>

      <div class="flex flex-shrink-0 items-center gap-2">
        <NextButton
          type="button"
          faded
          slate
          size="sm"
          :label="knowledgeContent.editLabel"
          @click="emit('edit', document)"
        />

        <ConfirmButton
          variant="faded"
          color="ruby"
          confirm-color="ruby"
          size="sm"
          :is-loading="isDeleting"
          :label="knowledgeContent.deleteLabel"
          :confirm-label="knowledgeContent.confirmDeleteLabel"
          :confirm-hint="knowledgeContent.deleteConfirmHint"
          @click="emit('delete', document)"
        />
      </div>
    </div>
  </article>
</template>
